import React, {useState} from 'react';

import {Button, ModalPopup, Tab, Tabs} from '@kit';

import {useCharacter} from '../character-context';
import {ChoiceField} from '../master/choice-field';
import {ClassList, classIconUrl} from '../master/class-list';
import {FeatureDetails} from '../master/feature-details';
import {averageHitDie, hitPointRollKey} from '../model/character';
import type {CharacterState} from '../model/character-state';
import {dieFormula} from '../model/dice';
import {multiclassBlockers} from '../model/multiclass';
import {isPreparedOnSheet} from '../model/spell-preparation';
import {useRoll} from './dice-roller';

import css from './sheet.module.css';

type LevelUp = {
	classId: string;
	classLevel: number;
	/** состояние до повышения: к нему возвращает отмена */
	previous: Pick<CharacterState, 'classes' | 'hitPointRolls'>;
};

type Props = {
	onClose(): void;
};

export const LevelUpModal: React.FC<Props> = ({onClose}) => {
	const {catalog, state, character, update} = useCharacter();
	const [levelUp, setLevelUp] = useState<LevelUp | null>(null);
	const [isAddingClass, setAddingClass] = useState(false);

	const raiseLevel = (classId: string) => {
		const existing = state.classes.find((entry) => entry.classId === classId);
		const classLevel = (existing?.level || 0) + 1;
		setLevelUp({classId, classLevel, previous: {classes: state.classes, hitPointRolls: state.hitPointRolls}});
		update((current) => ({
			classes: existing
				? current.classes.map((entry) => (entry.classId === classId ? {...entry, level: classLevel} : entry))
				: [...current.classes, {classId, level: 1}],
		}));
	};

	const cancelLevelUp = () => {
		if (levelUp) update(levelUp.previous);
		setLevelUp(null);
		setAddingClass(false);
	};

	const newClasses = catalog.classes.filter(
		(classData) => !state.classes.some((classLevel) => classLevel.classId === classData.id),
	);
	const header = levelUp
		? `${catalog.getClass(levelUp.classId)?.title}: ${levelUp.classLevel}-й уровень`
		: 'Повышение уровня';

	return (
		<ModalPopup open onClose={onClose} header={header} maxWidth={800}>
			{levelUp ? (
				<LevelUpSteps levelUp={levelUp} />
			) : isAddingClass ? (
				<>
					<p>Выберите новый класс для мультиклассирования:</p>
					<ClassList
						classes={newClasses}
						onSelect={raiseLevel}
						blockers={(classData) => multiclassBlockers(catalog, character, classData)}
					/>
				</>
			) : (
				<>
					<p>Какой класс получает уровень?</p>
					<ul className={css.levelUpClasses}>
						{state.classes.map(({classId, level}) => (
							<li key={classId}>
								<button type="button" className={css.levelUpClass} onClick={() => raiseLevel(classId)}>
									<img src={classIconUrl(classId)} alt="" width={40} height={40} />
									<span className={css.rowTitle}>{catalog.getClass(classId)?.title}</span>
									<span className={css.muted}>
										{level} → {level + 1}
									</span>
								</button>
							</li>
						))}
					</ul>
					{newClasses.length > 0 && <Button onClick={() => setAddingClass(true)}>Новый класс</Button>}
				</>
			)}
			<div className={css.modalButtons}>
				{levelUp && (
					<Button appearance="subtle" onClick={cancelLevelUp}>
						Отменить повышение
					</Button>
				)}
				{isAddingClass && !levelUp && (
					<Button appearance="subtle" onClick={() => setAddingClass(false)}>
						Назад
					</Button>
				)}
				<Button appearance="primary" onClick={onClose}>
					Готово
				</Button>
			</div>
		</ModalPopup>
	);
};

const LevelUpSteps: React.FC<{levelUp: LevelUp}> = ({levelUp}) => {
	const {features, character} = useCharacter();
	const classFeatures = features.classes.find((entry) => entry.classData.id === levelUp.classId);
	const newFeatures = (classFeatures?.features || []).filter((instance) => instance.level === levelUp.classLevel);
	const spellChoices = character.choices.filter(
		(choice) =>
			choice.owner.classId === levelUp.classId &&
			choice.group.type === 'spell' &&
			!isPreparedOnSheet(choice, character.getFeatureInstance(choice.featureKey)?.feature),
	);

	return (
		<Tabs>
			<Tab header="Особенности">
				{newFeatures.length ? (
					newFeatures.map((instance) => <FeatureDetails key={instance.key} instance={instance} />)
				) : (
					<p className={css.muted}>На этом уровне класс не даёт новых особенностей.</p>
				)}
			</Tab>
			<Tab header="Кости здоровья">
				<HitPointsStep levelUp={levelUp} />
			</Tab>
			<Tab header="Заклинания">
				{spellChoices.length ? (
					<div className={css.levelUpSpells}>
						{spellChoices.map((choice) => (
							<ChoiceField key={choice.key} choice={choice} />
						))}
					</div>
				) : (
					<p className={css.muted}>
						Класс не выбирает новые заклинания: подготовленные меняются во вкладке «Заклинания».
					</p>
				)}
			</Tab>
		</Tabs>
	);
};

const HitPointsStep: React.FC<{levelUp: LevelUp}> = ({levelUp}) => {
	const {catalog, state, character, update} = useCharacter();
	const roll = useRoll();
	const classData = catalog.getClass(levelUp.classId);
	if (!classData) return null;

	const key = hitPointRollKey(levelUp.classId, levelUp.classLevel);
	const rolled = state.hitPointRolls[key];
	const average = averageHitDie(classData.hitDie);
	const setValue = (value: number) =>
		update((current) => ({hitPointRolls: {...current.hitPointRolls, [key]: value}}));

	return (
		<div className={css.levelUpHitPoints}>
			<p>
				Кость здоровья {classData.genitiveTitle}: d{classData.hitDie}. К ней прибавляется модификатор
				Выносливости.
			</p>
			<div className={css.headerButtons}>
				<Button
					appearance="primary"
					onClick={() =>
						roll({
							title: `Кость здоровья d${classData.hitDie}: ${levelUp.classLevel}-й уровень`,
							formula: dieFormula(classData.hitDie),
							onTotal: setValue,
						})
					}
				>
					Бросить d{classData.hitDie}
				</Button>
				<Button onClick={() => setValue(average)}>Взять среднее ({average})</Button>
			</div>
			<p>
				{rolled == null ? 'Пока берётся среднее значение' : `На кости: ${rolled}`}. Максимум ПЗ:{' '}
				<b>{character.maxHitPoints}</b>.
			</p>
		</div>
	);
};
